import { ServiceItem, ServiceTransaction } from '../types';
import { waivedQuantity } from './projectLv';
import { isBackfilledAltEntry, isPostingEntry } from './serviceLogUtils';

/**
 * VERZICHT auf eine erbrachte, aber nicht abgerechnete Menge — die Regeln.
 *
 * Der Anlass ist ein wiederkehrender Fall aus der Buchhaltung: das Projekt ist
 * schlussgerechnet, eine Begehung steht aber weiter als offen (erbracht, nie
 * auf einem Beleg). Sie bleibt dann dauerhaft in der Buchhaltungsliste, im
 * Rechnungs-Picker, im Projektcheck und im Rechnungslauf stehen. Bis hierher
 * war der einzige Ausweg ein SQL-Eingriff von Hand
 * (supabase/fix_5983_…, supabase/fix_6211_…).
 *
 * WAS DER VERZICHT IST — und was er ausdruecklich NICHT ist:
 *
 *   Verzicht  = die Leistung wurde erbracht und wird NICHT mehr gefordert.
 *               `quantityUsed` bleibt unveraendert, der Leistungsnachweis
 *               zeigt sie weiter, der Begehungsrhythmus zaehlt sie weiter.
 *               Nur "abrechenbar" faellt weg.
 *
 *   Ruecknahme= die Buchung haette es nie geben duerfen (Bericht ausgefallen,
 *               doppelt gebucht). Dort muss `quantityUsed` SINKEN. Das ist ein
 *               anderer Vorgang und gehoert nicht hierher.
 *
 * Die beiden zu vermischen faelscht entweder den Leistungsstand (Ruecknahme
 * als Verzicht gebucht: die nie erbrachte Begehung steht fuer immer im
 * Nachweis) oder den Nachweis (Verzicht als Ruecknahme gebucht: die erbrachte
 * Begehung verschwindet aus dem Protokoll). Deshalb trennt schon die
 * Oberflaeche beides, und deshalb ist die Begruendung hier Pflicht.
 *
 * Pure Funktionen, kein Supabase, kein Date.now() — "jetzt" wird immer
 * hereingereicht. Das folgt invoiceLogic.ts und ist im Projekt die Bedingung
 * dafuer, dass ueberhaupt getestet werden kann (es gibt kein jsdom).
 */

/** Praefix der Protokollspur eines Verzichts. Siehe serviceLogUtils.isAccountingEntry. */
export const WAIVER_REFERENCE_PREFIX = 'VZ-';

/** Kuerzeste Begruendung, die noch eine ist. */
const MIN_REASON_LENGTH = 3;

export interface WaiverInput {
    /** Menge, auf die ZUSAETZLICH verzichtet wird (nicht der neue Gesamtstand). */
    quantity: number;
    /**
     * WELCHE Protokolleintraege gemeint sind. Optional — siehe
     * ServiceItem.waivedEntryIds: bei Stunden oder einer Teilmenge ohne eigenen
     * Eintrag gibt es keinen Termin zu benennen.
     *
     * Sind ids gesetzt, muss `quantity` ihrer Summe entsprechen. Die Menge hier
     * aus den Eintraegen zu BILDEN waere bequemer und genau die Sorte zweiter,
     * unsichtbarer Entscheidung, an der in diesem Repo schon mehrfach zwei
     * Mengen auseinandergelaufen sind — der Aufrufer sagt, was er meint, und
     * die Regel prueft es.
     */
    entryIds?: string[];
    /** Warum. Pflicht — siehe unten. */
    reason: string;
    actorName: string;
    actorId?: string;
    /** ISO-Zeitpunkt, hereingereicht. */
    now: string;
    /** Kennung des Protokolleintrags, hereingereicht (crypto.randomUUID im Aufrufer). */
    entryId: string;
    /** Buchungsreferenz, hereingereicht (WAIVER_REFERENCE_PREFIX + Kennung). */
    reference: string;
}

export interface WaiverPlan {
    /** Die Position mit gesetztem Verzicht — ersetzt die alte im LV. */
    service: ServiceItem;
    /** Spur im Leistungsprotokoll. Menge 0: es wurde nichts erbracht und nichts gebucht. */
    entry: ServiceTransaction;
    /** Text fuer die Projektnotiz. */
    noteText: string;
}

/**
 * Bewusst ein FLACHES Ergebnis und keine unterschiedene Vereinigung
 * (`{ok:true,plan} | {ok:false,error}`): das Projekt uebersetzt ohne
 * `strictNullChecks`, und dort verengt TypeScript eine Vereinigung ueber ein
 * boolesches Merkmal nicht — jeder Aufrufer bekaeme Fehler auf `error` und
 * `plan`. Die Aufrufer pruefen deshalb beides.
 */
export interface WaiverResult {
    ok: boolean;
    /** Nur bei `ok`. */
    plan?: WaiverPlan;
    /** Nur bei `!ok` — Klartext fuer die Oberflaeche. */
    error?: string;
}

/**
 * Menge, auf die an dieser Position ueberhaupt noch verzichtet werden kann:
 * erbracht minus abgerechnet minus bereits verzichtet.
 *
 * Die Reservierung eines offenen Rechnungsentwurfs geht hier BEWUSST NICHT
 * ein. Sie ist eine Idempotenz-Schranke des Rechnungslaufs und keine Aussage
 * ueber die Leistung; wer verzichten will, waehrend ein Entwurf dieselbe Menge
 * schon zieht, hat ein Entwurfsproblem und keins des Verzichts — der Aufrufer
 * warnt davor, statt es hier stillschweigend abzuziehen.
 */
export const waivableQuantity = (service: ServiceItem): number =>
    Math.max(
        0,
        (service.quantityUsed || 0) - (service.quantityBilled || 0) - waivedQuantity(service),
    );

/** Auf drei Nachkommastellen — dieselbe Toleranz wie im uebrigen Mengenrechnen. */
const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/** Mengengleichheit im ueblichen Rahmen dieses Moduls. */
const EPS = 0.0005;

/** Warum ein Protokolleintrag nicht zur Wahl steht. */
export type WaiverBlockReason = 'billed' | 'partly_billed' | 'waived' | 'legacy';

export interface WaivableEntry {
    entry: ServiceTransaction;
    /** Menge des Eintrags, gerundet. */
    quantity: number;
    /** Gesetzt heisst: nicht waehlbar. */
    blocked?: WaiverBlockReason;
}

/** Klartext zu jedem Grund — einmal, damit Oberflaeche und Fehlermeldung dasselbe sagen. */
export const WAIVER_BLOCK_TEXT: Record<WaiverBlockReason, string> = {
    billed: 'bereits abgerechnet',
    partly_billed: 'teilweise abgerechnet',
    waived: 'bereits nicht mehr abgerechnet',
    legacy: 'aus einer Alt-Rechnung übernommen',
};

/**
 * Die Protokolleintraege einer Position mit der Auskunft, auf welchen ueberhaupt
 * verzichtet werden kann.
 *
 * WARUM die Reihenfolge zaehlt: `quantityBilled` ist eine blanke Menge und
 * benennt keine Eintraege. Im ganzen Repo zehrt sie die AELTESTEN Mengen auf
 * (invoiceLogic, projectLv) — abgerechnet ist also der vordere Teil des
 * Protokolls. Wer auf eine Begehung aus diesem Teil "verzichtet", verzichtet in
 * Wahrheit auf die letzte offene: die Menge sinkt am Ende, die benannte
 * Begehung ist laengst bezahlt. Deshalb sind gedeckte Eintraege hier nicht
 * waehlbar, statt es dem Anwender zu ueberlassen.
 *
 * Buchungsvorgaenge (`BU-`, `VZ-`, `RN-`, `MB-`, manuelle Verbuchung) sind
 * keine erbrachte Leistung und fallen ganz heraus — auch aus der Deckung.
 * Aus einer Alt-Rechnung nachgetragene Zeilen (`ALT-`) bleiben sichtbar, aber
 * gesperrt: sie sind bereits abgegolten, obwohl `quantityBilled` nichts davon
 * weiss (die Deckung dafuer laeuft ueber die Alt-Buchungen).
 */
export const waivableEntries = (
    service: ServiceItem,
    log: ServiceTransaction[] | null | undefined,
): WaivableEntry[] => {
    const eigene = (log || []).filter(
        (t) =>
            t?.serviceId === service.id &&
            !isPostingEntry(t) &&
            (t.quantity || 0) > 0,
    );
    // Aeltester zuerst, Undatiertes ans Ende: undatierte Mengen sind der
    // Altbestand, ueber den ohnehin nur der Sammelnachtrag etwas weiss.
    const sortiert = [...eigene].sort((a, b) => {
        const da = a.date || '';
        const db = b.date || '';
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da.localeCompare(db);
    });

    const billed = service.quantityBilled || 0;
    const bereitsVerzichtet = new Set((service.waivedEntryIds || []).map(String));

    let vor = 0;
    return sortiert.map((entry) => {
        const quantity = round3(entry.quantity || 0);
        const nach = round3(vor + quantity);
        let blocked: WaiverBlockReason | undefined;
        if (bereitsVerzichtet.has(String(entry.id))) blocked = 'waived';
        else if (isBackfilledAltEntry(entry)) blocked = 'legacy';
        else if (nach <= billed + EPS) blocked = 'billed';
        else if (vor < billed - EPS) blocked = 'partly_billed';
        vor = nach;
        return { entry, quantity, blocked };
    });
};

/** Tag eines Eintrags fuer Protokolltext und Notiz — wie in serviceReversalLogic. */
const tagOf = (entry: ServiceTransaction): string => (entry.date || '').slice(0, 10) || 'ohne Datum';

/**
 * Einen Verzicht planen. Schreibt nichts, entscheidet nur.
 *
 * Zu viel zu verzichten ist ein FEHLER und wird nicht stillschweigend auf das
 * Moegliche gekuerzt. Ein stiller Deckel sieht fuer den Anwender aus wie ein
 * Erfolg — und beim naechsten Blick steht die Position immer noch offen, ohne
 * dass jemand erfuehre warum. Genau diese Klasse stiller Nicht-Wirkung war der
 * Anlass, den Vorgang ueberhaupt in die App zu holen.
 */
export const planWaiver = (
    service: ServiceItem,
    input: WaiverInput,
    log: ServiceTransaction[] | null | undefined = [],
): WaiverResult => {
    const available = round3(waivableQuantity(service));
    const quantity = round3(input.quantity);
    const reason = (input.reason || '').trim();

    if (!Number.isFinite(quantity) || quantity <= 0) {
        return { ok: false, error: 'Bitte eine Menge größer als 0 angeben.' };
    }
    if (available <= 0) {
        return { ok: false, error: 'An dieser Position ist nichts mehr offen, worauf verzichtet werden könnte.' };
    }
    if (quantity > available) {
        return {
            ok: false,
            error: `Es sind nur ${available} ${service.unit || ''} offen. Auf mehr kann nicht verzichtet werden.`.replace(
                '  ',
                ' ',
            ),
        };
    }
    if (reason.length < MIN_REASON_LENGTH) {
        // Ohne Grund ist der Verzicht im Streitfall wertlos, und er waere sonst
        // der bequemere Weg als die richtige Klaerung (Ruecknahme, Nachbuchen,
        // Ruecksprache mit dem Auftraggeber). Dieselbe Ueberlegung traegt schon
        // die Pflichtbegruendung bei "nicht abrechenbar" fuer versendete
        // Berichte.
        return { ok: false, error: 'Bitte eine Begründung angeben — sie steht später im Protokoll.' };
    }

    // WELCHE Leistungen gemeint sind. Optional, aber wenn benannt, dann streng:
    // ein Verzicht, der auf eine bereits bezahlte Begehung zeigt, senkt in
    // Wahrheit die letzte offene Menge und behauptet dabei etwas anderes.
    const ids = (input.entryIds || []).map(String).filter(Boolean);
    const gewaehlt: WaivableEntry[] = [];
    if (ids.length > 0) {
        const bekannt = new Map(waivableEntries(service, log).map((e) => [String(e.entry.id), e]));
        const gesehen = new Set<string>();
        for (const id of ids) {
            if (gesehen.has(id)) {
                return { ok: false, error: 'Ein Protokolleintrag wurde doppelt gewählt.' };
            }
            gesehen.add(id);
            const treffer = bekannt.get(id);
            if (!treffer) {
                return {
                    ok: false,
                    error: 'Ein gewählter Protokolleintrag gehört nicht zu dieser Position oder ist keine erbrachte Leistung.',
                };
            }
            if (treffer.blocked) {
                return {
                    ok: false,
                    error: `Der Eintrag vom ${tagOf(treffer.entry)} ist ${WAIVER_BLOCK_TEXT[treffer.blocked]} — auf ihn kann nicht verzichtet werden.`,
                };
            }
            gewaehlt.push(treffer);
        }
        const summe = round3(gewaehlt.reduce((sum, e) => sum + e.quantity, 0));
        if (Math.abs(summe - quantity) > EPS) {
            return {
                ok: false,
                error: `Die gewählten Einträge stehen für ${summe} ${service.unit || ''}, verzichtet werden sollen ${quantity}.`.replace(
                    '  ',
                    ' ',
                ),
            };
        }
    }

    const newWaived = round3(waivedQuantity(service) + quantity);
    const woher = gewaehlt.length > 0 ? ` (Leistung vom ${gewaehlt.map((e) => tagOf(e.entry)).join(', ')})` : '';

    return {
        ok: true,
        plan: {
            service: {
                ...service,
                quantityWaived: newWaived,
                waivedReason: reason,
                waivedAt: input.now,
                waivedBy: input.actorName,
                // ANGEHAENGT, nicht ersetzt: ein zweiter Verzicht auf derselben
                // Position meint eine andere Begehung, und die erste bleibt
                // verzichtet. quantityWaived waechst genauso.
                waivedEntryIds: [...(service.waivedEntryIds || []).map(String), ...gewaehlt.map((e) => String(e.entry.id))],
            },
            entry: {
                id: input.entryId,
                // Datum des VORGANGS, nicht der Leistung. Der Eintrag traegt
                // Menge 0 und faellt damit aus jeder Mengenrechnung heraus; er
                // ist reine Spur.
                date: input.now,
                serviceId: service.id,
                quantity: 0,
                performerId: input.actorId || '',
                performerName: input.actorName,
                isSubstitution: false,
                description: `Verzicht auf ${quantity} ${service.unit || ''}${woher} — ${reason}`.replace('  ', ' '),
                bookedAt: input.now,
                bookedBy: input.actorName,
                bookedById: input.actorId,
                bookingReference: input.reference,
            },
            noteText:
                `Nicht mehr abgerechnet: ${quantity} ${service.unit || ''} `.replace('  ', ' ') +
                `„${service.title}" (${service.lvPosition || 'ohne LV-Nr.'})${woher}. Grund: ${reason}`,
        },
    };
};

/**
 * Einen Verzicht wieder aufheben.
 *
 * Setzt eine ausdrueckliche 0 statt `undefined`: nur ein gesetzter Schluessel
 * erreicht die Datenbank (siehe cloudService, save_project_children_tx baut
 * seine Spaltenliste aus jsonb_object_keys). Mit `undefined` bliebe der alte
 * Wert stehen und das Aufheben waere nach dem Neuladen zurueck.
 */
export const planWaiverRelease = (
    service: ServiceItem,
    input: Omit<WaiverInput, 'quantity' | 'reason'> & { reason?: string },
): WaiverResult => {
    const current = waivedQuantity(service);
    if (current <= 0) {
        return { ok: false, error: 'An dieser Position ist kein Verzicht hinterlegt.' };
    }
    const reason = (input.reason || '').trim();
    return {
        ok: true,
        plan: {
            service: {
                ...service,
                quantityWaived: 0,
                waivedReason: undefined,
                waivedAt: undefined,
                waivedBy: undefined,
                // Leere LISTE, nicht undefined — sonst zeigte die Zuordnung
                // weiter auf Begehungen, auf die niemand mehr verzichtet.
                waivedEntryIds: [],
            },
            entry: {
                id: input.entryId,
                date: input.now,
                serviceId: service.id,
                quantity: 0,
                performerId: input.actorId || '',
                performerName: input.actorName,
                isSubstitution: false,
                description:
                    `Verzicht aufgehoben (${current} ${service.unit || ''})`.replace('  ', ' ') +
                    (reason ? ` — ${reason}` : ''),
                bookedAt: input.now,
                bookedBy: input.actorName,
                bookedById: input.actorId,
                bookingReference: input.reference,
            },
            noteText:
                `Verzicht aufgehoben: ${current} ${service.unit || ''} `.replace('  ', ' ') +
                `„${service.title}" (${service.lvPosition || 'ohne LV-Nr.'}) steht wieder zur Abrechnung.` +
                (reason ? ` Grund: ${reason}` : ''),
        },
    };
};
